<template>
  <div ref="rootEl" class="editable-title">
    <span class="editable-title__input" v-if="html && !editing" v-html="modelValue" @click="startEditing"></span>
    <span
      v-else
      ref="inputEl"
      class="editable-title__input"
      :contenteditable="html ? 'true' : 'plaintext-only'"
      @click="startEditing"
      @beforeinput="onBeforeInput"
      @keydown.enter="onEnter"
      @input="onInput"
    >
    </span>

    <div v-if="showToolbar" class="editable-title__toolbar">
      <button class="editable-title__edit-btn" type="button" @click="selectAll">
        <PencilIcon width="16" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import PencilIcon from 'app/assets/images/common/pencil.svg?component'

const props = withDefaults(
  defineProps<{
    modelValue: string
    maxLength?: number
    showToolbar?: boolean
    html?: boolean
  }>(),
  {
    maxLength: 60,
    showToolbar: true,
    html: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  onBlur: [{ field: 'title' | 'prelude'; value: string }]
}>()

const rootEl = ref<HTMLElement>()
const inputEl = ref<HTMLElement>()
const editing = ref(false)

function getContent(): string {
  if (!inputEl.value) return ''
  return props.html ? inputEl.value.innerHTML : inputEl.value.innerText
}

function setContent(value: string) {
  if (!inputEl.value) return
  if (props.html) inputEl.value.innerHTML = value
  else inputEl.value.innerText = value
}

onMounted(() => setContent(props.modelValue))
onClickOutside(rootEl, () => stopEditing())

function startEditing() {
  const wasEditing = editing.value
  editing.value = true

  if (wasEditing || !props.html) return

  nextTick(() => {
    setContent(props.modelValue)
    inputEl.value?.focus()
  })
}

function stopEditing() {
  if (!editing.value) return

  editing.value = false
  inputEl.value?.blur()
  emit('onBlur', { field: 'title', value: getContent() })
}

function selectAll() {
  editing.value = true

  nextTick(() => {
    setContent(props.modelValue)
    inputEl.value?.focus()
    if (inputEl.value) window.getSelection()?.selectAllChildren(inputEl.value)
  })
}

function onEnter(e: KeyboardEvent) {
  if (!props.html) {
    e.preventDefault()
    stopEditing()
  }
}

function onInput() {
  emit('update:modelValue', getContent())
}

function onBeforeInput(e: InputEvent) {
  const currentLen = inputEl.value?.innerText?.length ?? 0
  const newLength = currentLen + (e.data?.length ?? 0)
  if (newLength > props.maxLength) {
    e.preventDefault()
  }
}
</script>

<style lang="scss" scoped>
.editable-title {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;

  .editable-title__input {
    max-width: calc(100vw - var(--site-px) * 2);
    border-radius: 15px;
    padding: $spacing-2 $spacing-3;
    border: 1px dashed $grey-100;

    &:focus {
      border: 1px solid $green-200;
      outline: none;
    }
  }
}

.editable-title__toolbar {
  position: absolute;
  top: -0.6rem;
  right: -0.4rem;
  background: $white;
  height: 16px;
}

.editable-title__edit-btn {
  display: inline-flex;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;

  :deep(svg path) {
    stroke: $brand-tertiary;
  }
}
</style>
